import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import React, { useState, useCallback, useRef } from 'react';
import { Check, Rocket, Activity, Package, Container, Folder, Cpu, ChevronRight, Clock, Monitor, Cloud, ArrowLeft, Search, LoaderCircle, Globe, Terminal, PlayCircle, Star, Download,
  Layers, Lock, Database, Trash2, X, Info, Settings, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';
import { generateRandomSuffix, validateSubdomainPrefix } from '../../utils/subdomainHelper';


const DockerIcon = ({ size = 24, className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" width={size} height={size} stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
  </svg>
);

export default function DockerHubDeploy({
  user, onStatusMessage, onBack, api,
  beginDeploymentTracking, refreshDeploymentSession, setCurrentSessionProgress, onTabChange
}) {
  
  const [hubAppName, setHubAppName] = useState('');
  const [hubAppNameError, setHubAppNameError] = useState('');
  const [platformSuffix] = useState(() => generateRandomSuffix());
  const [subdomainPrefix, setSubdomainPrefix] = useState('');
  const [subdomainError, setSubdomainError] = useState('');
  const [hubImage, setHubImage] = useState('');
  const [hubCategory, setHubCategory] = useState('web');
  const [hubCpus, setHubCpus] = useState('1');
  const [hubMemory, setHubMemory] = useState('1024');
  const [hubDomain, setHubDomain] = useState('');
  const [hubDomainError, setHubDomainError] = useState('');

  const handleSubdomainChange = (e) => {
    const val = e.target.value;
    setSubdomainPrefix(val);
    const res = validateSubdomainPrefix(val);
    setSubdomainError(res.valid ? '' : res.error);
  };

  const validateHubDomain = (val) => {
    if (!val || !val.trim()) {
      setHubDomainError('');
      return true;
    }
    if (/devorants\.com$/i.test(val.trim())) {
      setHubDomainError('devorants.com subdomains cannot be used as custom domains.');
      return false;
    }
    setHubDomainError('');
    return true;
  };

  const handleHubDomainChange = (e) => {
    const val = e.target.value;
    setHubDomain(val);
    validateHubDomain(val);
  };
  const [selectedHubPort, setSelectedHubPort] = useState('');
  const [hubEnvKey, setHubEnvKey] = useState('');
  const [hubEnvValue, setHubEnvValue] = useState('');
  const [hubVolumeName, setHubVolumeName] = useState('');
  const [hubVolumeContainerPath, setHubVolumeContainerPath] = useState('');
  const [hubVolumeSize, setHubVolumeSize] = useState('1');
  const [hubPortDetecting, setHubPortDetecting] = useState(false);
  const [hubDescriptionExpanded, setHubDescriptionExpanded] = useState(true);
  const [hubEnvExpanded, setHubEnvExpanded] = useState(true);
  const [loading, setLoading] = useState(false);

  const validateHubAppName = (val) => {
    if (!val) {
      setHubAppNameError('');
      return false;
    }
    if (/\s/.test(val)) {
      setHubAppNameError('Spaces are not allowed. Use hyphens (-) instead.');
      return false;
    }
    if (/[A-Z]/.test(val)) {
      setHubAppNameError('Uppercase letters are not allowed. Use lowercase only.');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(val)) {
      setHubAppNameError('Only lowercase letters, numbers, and hyphens (-) are allowed.');
      return false;
    }
    setHubAppNameError('');
    return true;
  };

  const handleHubAppNameChange = (e) => {
    const val = e.target.value;
    setHubAppName(val);
    validateHubAppName(val);
  };
  
  const handlePortChange = (e) => setSelectedHubPort(e.target.value);

  const [hubSearchQuery, setHubSearchQuery] = useState('');
  const [hubSearchResults, setHubSearchResults] = useState([]);
  const [hubSearchLoading, setHubSearchLoading] = useState(false);
  const [hubSelectedImage, setHubSelectedImage] = useState(null);
  const [hubAppDetails, setHubAppDetails] = useState({ name: '', image: '', tag: 'latest' });
  const [hubAvailableTags, setHubAvailableTags] = useState(['latest']);
  const [hubTagsLoading, setHubTagsLoading] = useState(false);
  const [hubMode, setHubMode] = useState('search'); // 'search' | 'configure' | 'deploying'
  const [hubDeployStep, setHubDeployStep] = useState(0);
  const [hubDeployError, setHubDeployError] = useState('');
  const [hubEnvVars, setHubEnvVars] = useState([]);
  const [hubVolumes, setHubVolumes] = useState([]);
  const [hubPorts, setHubPorts] = useState([]);
  const [deploymentSessionId, setDeploymentSessionId] = useState(null);
  const [deploymentSession, setDeploymentSession] = useState(null);
  const [buildLogContent, setBuildLogContent] = useState('');
  const [loadingBuildLog, setLoadingBuildLog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  
  const [hubSelectedTag, setHubSelectedTag] = useState('');
  const [hubImageInfo, setHubImageInfo] = useState(null);
  const [hubDetailLoading, setHubDetailLoading] = useState(false);
  const [hubDetectedPorts, setHubDetectedPorts] = useState([]);
  const [hubPortFetchFailed, setHubPortFetchFailed] = useState(false);
  const [hubInternalPort, setHubInternalPort] = useState('');
  const [hubTags, setHubTags] = useState([]);
  
  const pollIntervalRef = useRef(null);
  const hubSearchTimerRef = useRef(null);
  const onStatusMessageRef = useRef(onStatusMessage);
  const topContainerRef = useRef(null);

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays}d ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

const handleHubSearch = useCallback(async (query, page = 1) => {
    if (!query || query.trim().length < 2) {
      setHubSearchResults([]);
      return;
    }
    setHubSearchLoading(true);
    try {
      const data = await api.searchDockerHub(query.trim(), page, 12);
      setHubSearchResults(data.results || []);
    } catch (err) {
      onStatusMessageRef.current?.(`Search failed: ${err.message}`, 'error');
      setHubSearchResults([]);
    } finally {
      setHubSearchLoading(false);
    }
  }, [api]);

const handleHubSearchInput = (value) => {
    setHubSearchQuery(value);
    if (hubSearchTimerRef.current) clearTimeout(hubSearchTimerRef.current);
    hubSearchTimerRef.current = setTimeout(() => {
      handleHubSearch(value, 1);
    }, 400);
  };

const handleSelectHubImage = async (image) => {
    setHubSelectedImage(image);
    setHubMode('detail');
    scrollToTop();
    setHubTagsLoading(true);
    setHubDetailLoading(true);
    setHubSelectedTag('');
    setHubImageInfo(null);
    setHubDetectedPorts([]);
    setHubPortFetchFailed(false);

    // Fetch tags and initial image inspection in parallel
    const [tagsResult, inspectResult] = await Promise.allSettled([
      api.getDockerHubTags(image.name, 1, 50),
      api.inspectDockerHubImage(image.name, 'latest')
    ]);

    if (tagsResult.status === 'fulfilled') {
      setHubTags(tagsResult.value.tags || []);
    } else {
      onStatusMessage(`Failed to fetch tags: ${tagsResult.reason?.message || 'Unknown error'}`, 'error');
      setHubTags([]);
    }

    if (inspectResult.status === 'fulfilled') {
      setHubImageInfo(inspectResult.value);
      if (inspectResult.value.exposedPorts && inspectResult.value.exposedPorts.length > 0) {
        setHubDetectedPorts(inspectResult.value.exposedPorts);
        const tcpPort = inspectResult.value.exposedPorts.find(p => p.protocol === 'tcp') || inspectResult.value.exposedPorts[0];
        if (tcpPort) {
          setHubInternalPort(String(tcpPort.port));
        }
        setHubPortFetchFailed(false);
      } else {
        setHubDetectedPorts([]);
        setHubInternalPort('');
        setHubPortFetchFailed(true);
      }
    } else {
      setHubDetectedPorts([]);
      setHubInternalPort('');
      setHubPortFetchFailed(true);
    }

    setHubTagsLoading(false);
    setHubDetailLoading(false);
  };

const handleSelectTag = async (tagName) => {
    setHubSelectedTag(tagName);
    const fullImage = `${hubSelectedImage.name}:${tagName}`;
    setHubImage(fullImage);
    if (!hubAppName) {
      const baseName = hubSelectedImage.name.split('/').pop().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      setHubAppName(baseName.substring(0, 50));
    }

    // Re-inspect with the specific tag to get accurate port info
    if (tagName !== 'latest') {
      setHubPortDetecting(true);
      try {
        const inspection = await api.inspectDockerHubImage(hubSelectedImage.name, tagName);
        setHubImageInfo(inspection);
        if (inspection.exposedPorts && inspection.exposedPorts.length > 0) {
          setHubDetectedPorts(inspection.exposedPorts);
          const tcpPort = inspection.exposedPorts.find(p => p.protocol === 'tcp') || inspection.exposedPorts[0];
          if (tcpPort) {
            setHubInternalPort(String(tcpPort.port));
          }
          setHubPortFetchFailed(false);
        } else {
          setHubDetectedPorts([]);
          setHubInternalPort('');
          setHubPortFetchFailed(true);
        }
      } catch (err) {
        setHubDetectedPorts([]);
        setHubInternalPort('');
        setHubPortFetchFailed(true);
      } finally {
        setHubPortDetecting(false);
      }
    }
  };

  function scrollToTop() {
    const doScroll = () => {
      try {
        if (topContainerRef.current) {
          topContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        const scrollContainers = [
          document.querySelector('.app-content-area'),
          document.querySelector('.dashboard-content'),
          document.querySelector('.main-content'),
          document.querySelector('.vercel-form-body'),
          document.documentElement,
          document.body
        ];
        scrollContainers.forEach(el => {
          if (el) {
            if (typeof el.scrollTo === 'function') {
              el.scrollTo({ top: 0, behavior: 'smooth' });
            }
            el.scrollTop = 0;
          }
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (_) {}
    };

    doScroll();
    setTimeout(doScroll, 80);
    setTimeout(doScroll, 200);
  }

  const handleHubContinueToDeploy = () => {
    if (!hubSelectedTag && hubTags.length > 0) {
      // Auto-select 'latest' if no tag selected yet
      const latestTag = hubTags.find(t => t.name === 'latest');
      if (latestTag) {
        handleSelectTag('latest');
      } else {
        handleSelectTag(hubTags[0].name);
      }
    }
    setHubMode('manual');
    scrollToTop();
  };

const handleAddHubVolume = () => {
    if (!hubVolumeContainerPath.trim() || !hubVolumeName.trim()) {
      onStatusMessage('Volume Name and Container Path are required', 'error');
      return;
    }
    const path = hubVolumeContainerPath.trim();
    const name = hubVolumeName.trim().replace(/[^a-zA-Z0-9-_]/g, '').toLowerCase();
    const sizeLimit = hubVolumeSize.trim() || '5GB';

    if (!path.startsWith('/')) {
      onStatusMessage('Container path must be absolute (start with /)', 'error');
      return;
    }
    // Prevent duplicate paths
    if (hubVolumes.some(v => v.path === path || v === path || (v.mount && v.mount.endsWith(`:${path}`)))) {
      onStatusMessage('This container path is already configured', 'warning');
      return;
    }
    // Prevent duplicate names
    if (hubVolumes.some(v => v.name === name)) {
      onStatusMessage('This volume name is already used', 'warning');
      return;
    }

    const volumeObject = {
      name: name,
      path: path,
      sizeLimit: sizeLimit,
      isNamedVolume: true,
      mount: `${name}:${path}`
    };

    setHubVolumes([...hubVolumes, volumeObject]);
    setHubVolumeName('');
    setHubVolumeContainerPath('');
    setHubVolumeSize('5GB');
  };

const handleRemoveHubVolume = (index) => {
    setHubVolumes(hubVolumes.filter((_, i) => i !== index));
  };

const handleHubDeploy = async (e) => {
    e.preventDefault();
    if (!hubAppName || !validateHubAppName(hubAppName) || !hubImage) {
      onStatusMessage(hubAppNameError || 'Please fill in all fields properly', 'error');
      return;
    }

    if (hubVolumes.length === 0) {
      onStatusMessage('At least one persistent volume mapping is required for Docker Hub deployments', 'error');
      return;
    }

    if (subdomainPrefix && !validateSubdomainPrefix(subdomainPrefix).valid) {
      onStatusMessage(subdomainError || 'Invalid subdomain format.', 'error');
      return;
    }

    if (hubDomain && !validateHubDomain(hubDomain)) {
      onStatusMessage(hubDomainError || 'devorants.com subdomains cannot be used as custom domains.', 'error');
      return;
    }

    const resolvedPrefix = (subdomainPrefix && subdomainPrefix.trim())
      ? subdomainPrefix.trim().toLowerCase()
      : (hubAppName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    const autoPlatformDomain = `${resolvedPrefix}-${platformSuffix}.devorants.com`;
    const finalDomain = (hubDomain && hubDomain.trim()) ? hubDomain.trim() : autoPlatformDomain;

    setLoading(true);
    const deploymentId = beginDeploymentTracking('hub', hubAppName);

    // Build env object from the key-value array
    const envObj = {};
    hubEnvVars.forEach(v => {
      if (v.key) envObj[v.key] = v.value;
    });

    try {
      api.deployDockerHub(hubAppName, hubImage, hubInternalPort, hubCategory, hubCpus, hubMemory, {
        deploymentId,
        domain: finalDomain,
        publicPort: selectedHubPort ? parseInt(selectedHubPort) : undefined,
        env: envObj,
        volumes: hubVolumes
      }).then(
        (response) => {
          if (response && response.cancelled) return;
          setHubAppName('');
          setSubdomainPrefix('');
          setSubdomainError('');
          setHubImage('');
          setHubInternalPort('80');
          setHubCategory('backend');
          setHubCpus('1');
          setHubMemory('512m');
          setHubDomain('');
          setHubDomainError('');
          setSelectedHubPort('');
          setHubImageInfo(null);
          setHubPortFetchFailed(false);
          setHubSelectedTag('');
          setHubSelectedImage(null);
          setHubDetectedPorts([]);
          setHubTags([]);
          setHubEnvVars([]);
          setHubEnvKey('');
          setHubEnvValue('');
          setHubVolumes([]);
          setHubVolumeContainerPath('');
          refreshDeploymentSession(deploymentId);
          // Navigate to dashboard after a short delay so progress tracking can finish
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('switch-to-dashboard'));
            if (onTabChange) onTabChange('deployments');
          }, 1500);
        },
        (err) => {
          onStatusMessage(`Error: ${err.message}`, 'error');
          refreshDeploymentSession(deploymentId);
        }
      ).finally(() => {
        setLoading(false);
        setCurrentSessionProgress(100);
      });
    } catch (err) {
      onStatusMessage(`Error: ${err.message}`, 'error');
      setLoading(false);
    }
  };


  return (
          <div className="vercel-form-body" ref={topContainerRef}>
            <button
              className="vercel-back-btn-new"
              onClick={() => {
                if (hubMode === 'detail') {
                  setHubMode('search');
                  setHubSelectedImage(null);
                  setHubTags([]);
                  setHubImageInfo(null);
                  setHubPortFetchFailed(false);
                  setHubSelectedTag('');
                } else if (hubMode === 'manual') {
                  if (hubSelectedImage) {
                    setHubMode('detail');
                  } else {
                    setHubMode('search');
                  }
                } else {
                  setHubMode('search');
                  setHubSelectedImage(null);
                  setHubTags([]);
                  setHubImageInfo(null);
                  setHubPortFetchFailed(false);
                  setHubSelectedTag('');
                  onBack();
                }
              }}
            >
              <ArrowLeft size={16} />
              <span>{hubMode === 'detail' ? 'Back to search' : hubMode === 'manual' ? 'Back to image details' : 'Back to selection'}</span>
            </button>
            <div className="dashboard-filter-tabs-container" style={{ marginBottom: '1.5rem', justifyContent: 'flex-start' }}>
              <div className="dashboard-filter-tabs">
                <button type="button" className={`filter-tab ${hubMode === 'search' ? 'active' : ''}`} onClick={() => { setHubMode('search'); setHubSelectedImage(null); setHubTags([]); setHubImageInfo(null); setHubSelectedTag(''); }}>
                  <Search size={16} /> Search Hub
                </button>
                {hubSelectedImage && (
                  <button type="button" className={`filter-tab ${hubMode === 'detail' ? 'active' : ''}`} onClick={() => { setHubMode('detail'); scrollToTop(); }}>
                    <Info size={16} /> {hubSelectedImage.name}
                  </button>
                )}
                <button type="button" className={`filter-tab ${hubMode === 'manual' ? 'active' : ''}`} onClick={() => { setHubMode('manual'); scrollToTop(); }}>
                  <Terminal size={16} /> Deploy
                </button>
              </div>
            </div>

            {hubMode === 'search' && (
              <div className="vercel-premium-form">
                <div className="dashboard-search-wrapper" style={{ height: '56px' }}>
                  <div className="dashboard-search-icon-box"><Search size={20} /></div>
                  <input type="text" className="dashboard-search-input" placeholder="Search Docker Hub (e.g. nginx, node, python)..." value={hubSearchQuery} onChange={(e) => handleHubSearchInput(e.target.value)} />
                  {hubSearchLoading && <LoaderCircle size={20} className="deploy-spin" style={{ color: 'var(--primary)' }} />}
                </div>

                {hubSearchResults.length > 0 && (
                  <div className="hub-results-grid">
                    {hubSearchResults.map((img) => (
                      <div key={img.name} className="hub-result-card" onClick={() => handleSelectHubImage(img)}>
                        <div className="hub-result-header">
                          <div className="hub-result-name">
                            <Cloud size={16} /> {img.name}
                          </div>
                          <div className="hub-meta-item">★ {img.starCount}</div>
                        </div>
                        <div className="hub-result-desc">{img.description || "No description provided."}</div>
                        <div className="hub-result-meta">
                          <div className="hub-meta-item">Pulls: {img.pullCount > 1000 ? (img.pullCount / 1000).toFixed(1) + 'k' : img.pullCount}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {hubMode === 'detail' && hubSelectedImage && (
              <div className="vercel-premium-form">
                {hubDetailLoading ? (
                  <div style={{ padding: '3rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                    <LoaderCircle size={40} className="deploy-spin" style={{ color: 'var(--primary)' }} />
                    <div style={{ fontWeight: 700, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Fetching image details from Docker Hub...</div>
                  </div>
                ) : (
                  <>
                    {/* ── Hero Card ── */}
                    <div className="hub-detail-hero">
                      <div className="hub-detail-hero-top">
                        <div className="hub-detail-hero-icon">
                          <DockerIcon size={36} />
                        </div>
                        <div className="hub-detail-hero-info">
                          <div className="hub-detail-hero-name">{hubSelectedImage.name}</div>
                          <div className="hub-detail-hero-desc">
                            {hubImageInfo?.repoInfo?.description || hubSelectedImage.description || 'No description available.'}
                          </div>
                        </div>
                      </div>
                      <div className="hub-detail-hero-stats">
                        {hubImageInfo?.repoInfo?.isOfficial && (
                          <div className="hub-detail-stat official"><CheckCircle2 size={14} /> Official Image</div>
                        )}
                        {hubImageInfo?.repoInfo?.isPrivate !== undefined && (
                          <div className={`hub-detail-stat ${hubImageInfo.repoInfo.isPrivate ? '' : ''}`}>
                            {hubImageInfo.repoInfo.isPrivate ? <Lock size={13} /> : <Globe size={13} />}
                            {hubImageInfo.repoInfo.isPrivate ? 'Private' : 'Public'}
                          </div>
                        )}
                        {hubImageInfo?.repoInfo?.user && (
                          <div className="hub-detail-stat">
                            <Monitor size={13} /> {hubImageInfo.repoInfo.user}
                          </div>
                        )}
                        <div className="hub-detail-stat">
                          <span className="hub-detail-stat-num">★ {(hubImageInfo?.repoInfo?.starCount || hubSelectedImage.starCount || 0).toLocaleString()}</span>
                        </div>
                        <div className="hub-detail-stat">
                          <span className="hub-detail-stat-num">
                            ↓ {(() => { const c = hubImageInfo?.repoInfo?.pullCount || hubSelectedImage.pullCount || 0; return c > 1000000 ? (c / 1000000).toFixed(1) + 'M' : c > 1000 ? (c / 1000).toFixed(1) + 'K' : c; })()}
                          </span> pulls
                        </div>
                        {hubImageInfo?.repoInfo?.lastUpdated && (
                          <div className="hub-detail-stat"><Clock size={13} /> Updated {new Date(hubImageInfo.repoInfo.lastUpdated).toLocaleDateString()}</div>
                        )}
                      </div>
                    </div>

                    {/* ── Full Description ── */}
                    {hubImageInfo?.repoInfo?.fullDescription && (
                      <div className="hub-detail-info-block">
                        <div
                          className="hub-detail-block-title hub-detail-collapsible-title"
                          onClick={() => setHubDescriptionExpanded(!hubDescriptionExpanded)}
                        >
                          <Info size={15} />
                          Documentation
                          <ChevronRight
                            size={16}
                            className={`hub-detail-chevron ${hubDescriptionExpanded ? 'expanded' : ''}`}
                          />
                        </div>
                        {hubDescriptionExpanded && (
                          <div className="hub-detail-full-desc">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              className="hub-markdown"
                              components={{
                                code({ node, inline, className, children, ...props }) {
                                  const match = /language-(\w+)/.exec(className || '');
                                  const codeStr = String(children).trim();

                                  // Check if it looks like an env variable
                                  const isEnvVar =
                                    codeStr.includes('=') ||
                                    /^[A-Z_][A-Z0-9_]*$/.test(codeStr) ||
                                    codeStr.startsWith('MYSQL_') ||
                                    codeStr.startsWith('POSTGRES_') ||
                                    codeStr.startsWith('REDIS_') ||
                                    codeStr.startsWith('MONGO_') ||
                                    codeStr.startsWith('DB_') ||
                                    codeStr.startsWith('ENV_');

                                  return !inline && match ? (
                                    <pre className={isEnvVar ? 'hub-markdown-pre-env' : ''}>
                                      <code className={className} {...props}>
                                        {children}
                                      </code>
                                    </pre>
                                  ) : (
                                    <code
                                      className={`${className || ''} ${isEnvVar ? 'hub-markdown-code-env' : ''}`}
                                      {...props}
                                    >
                                      {children}
                                    </code>
                                  );
                                }
                              }}
                            >
                              {hubImageInfo.repoInfo.fullDescription}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Image Configuration Metadata Grid ── */}
                    {hubImageInfo && (
                      <div className="hub-detail-metadata">
                        <div className="hub-detail-section-title">Image Configuration</div>
                        <div className="hub-detail-meta-grid">
                          {hubImageInfo.architecture && (
                            <div className="hub-detail-meta-card">
                              <div className="hub-detail-meta-icon"><Cpu size={16} /></div>
                              <div className="hub-detail-meta-info">
                                <div className="hub-detail-meta-label">Architecture / OS</div>
                                <div className="hub-detail-meta-val">{hubImageInfo.architecture}/{hubImageInfo.os || 'linux'}</div>
                              </div>
                            </div>
                          )}
                          {hubImageInfo.layerCount > 0 && (
                            <div className="hub-detail-meta-card">
                              <div className="hub-detail-meta-icon"><Layers size={16} /></div>
                              <div className="hub-detail-meta-info">
                                <div className="hub-detail-meta-label">Layers</div>
                                <div className="hub-detail-meta-val">{hubImageInfo.layerCount} layers</div>
                              </div>
                            </div>
                          )}
                          {hubImageInfo.workingDir && (
                            <div className="hub-detail-meta-card">
                              <div className="hub-detail-meta-icon"><Folder size={16} /></div>
                              <div className="hub-detail-meta-info">
                                <div className="hub-detail-meta-label">Working Directory</div>
                                <div className="hub-detail-meta-val">{hubImageInfo.workingDir}</div>
                              </div>
                            </div>
                          )}
                          {hubImageInfo.user && (
                            <div className="hub-detail-meta-card">
                              <div className="hub-detail-meta-icon"><Monitor size={16} /></div>
                              <div className="hub-detail-meta-info">
                                <div className="hub-detail-meta-label">Run As User</div>
                                <div className="hub-detail-meta-val">{hubImageInfo.user}</div>
                              </div>
                            </div>
                          )}
                          {hubImageInfo.dockerVersion && (
                            <div className="hub-detail-meta-card">
                              <div className="hub-detail-meta-icon"><Container size={16} /></div>
                              <div className="hub-detail-meta-info">
                                <div className="hub-detail-meta-label">Docker Version</div>
                                <div className="hub-detail-meta-val">{hubImageInfo.dockerVersion}</div>
                              </div>
                            </div>
                          )}
                          {hubImageInfo.created && (
                            <div className="hub-detail-meta-card">
                              <div className="hub-detail-meta-icon"><Clock size={16} /></div>
                              <div className="hub-detail-meta-info">
                                <div className="hub-detail-meta-label">Created</div>
                                <div className="hub-detail-meta-val">{new Date(hubImageInfo.created).toLocaleString()}</div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* ── Exposed Ports ── */}
                        <div className="hub-detail-section-title" style={{ marginTop: '0.25rem' }}>Networking</div>
                        {hubDetectedPorts.length > 0 ? (
                          <div className="hub-detail-info-block">
                            <div className="hub-detail-block-title"><Globe size={15} /> Exposed Ports</div>
                            <div className="hub-info-port-chips">
                              {hubDetectedPorts.map((p, i) => (
                                <span key={i} className="hub-port-chip active">{p.port}/{p.protocol}</span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="hub-detail-info-block" style={{ borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.04)' }}>
                            <div className="hub-detail-block-title" style={{ color: 'var(--accent, #f59e0b)' }}><AlertCircle size={15} /> No Exposed Ports Declared</div>
                            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                              This image does not declare any exposed ports in its configuration. You will need to enter the internal port manually when deploying.
                            </div>
                          </div>
                        )}

                        {/* ── Startup Command ── */}
                        {(hubImageInfo.entrypoint?.length > 0 || hubImageInfo.cmd?.length > 0) && (
                          <>
                            <div className="hub-detail-section-title" style={{ marginTop: '0.25rem' }}>Startup</div>
                            <div className="hub-detail-info-block">
                              <div className="hub-detail-block-title"><Terminal size={15} /> Command</div>
                              <div className="hub-info-code">
                                {hubImageInfo.entrypoint?.length > 0 && (
                                  <div><span className="hub-info-code-label">ENTRYPOINT</span> {hubImageInfo.entrypoint.join(' ')}</div>
                                )}
                                {hubImageInfo.cmd?.length > 0 && (
                                  <div><span className="hub-info-code-label">CMD</span> {hubImageInfo.cmd.join(' ')}</div>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        {/* ── Volumes ── */}
                        {hubImageInfo.volumes?.length > 0 && (
                          <>
                            <div className="hub-detail-section-title" style={{ marginTop: '0.25rem' }}>Storage</div>
                            <div className="hub-detail-info-block">
                              <div className="hub-detail-block-title"><Package size={15} /> Volume Mounts ({hubImageInfo.volumes.length})</div>
                              <div className="hub-info-code">
                                {hubImageInfo.volumes.map((v, i) => <div key={i}>{v}</div>)}
                              </div>
                            </div>
                          </>
                        )}

                        {/* ── Environment Variables (ALL) ── */}
                        {hubImageInfo.env?.length > 0 && (
                          <>
                            <div className="hub-detail-section-title" style={{ marginTop: '0.25rem' }}>Environment Variables</div>
                            <div className="hub-detail-info-block">
                              <div className="hub-detail-block-title"><Settings size={15} /> All Variables ({hubImageInfo.env.length})</div>
                              <div className="hub-info-code" style={{ maxHeight: '280px' }}>
                                {hubImageInfo.env.map((e, i) => (
                                  <div key={i}>
                                    <span className="hub-info-code-label">{e.key}</span>
                                    <span style={{ color: 'var(--text-tertiary)' }}>=</span>
                                    <span style={{ color: 'var(--text-secondary)' }}>{e.value || <em style={{ opacity: 0.5 }}>empty</em>}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        )}

                        {/* ── Labels ── */}
                        {hubImageInfo.labels && Object.keys(hubImageInfo.labels).length > 0 && (
                          <>
                            <div className="hub-detail-section-title" style={{ marginTop: '0.25rem' }}>Labels</div>
                            <div className="hub-detail-info-block">
                              <div className="hub-detail-block-title"><Activity size={15} /> Image Labels ({Object.keys(hubImageInfo.labels).length})</div>
                              <div className="hub-info-code" style={{ maxHeight: '200px' }}>
                                {Object.entries(hubImageInfo.labels).map(([k, v], i) => (
                                  <div key={i}>
                                    <span className="hub-info-code-label">{k}</span>
                                    <span style={{ color: 'var(--text-tertiary)' }}>=</span>
                                    <span style={{ color: 'var(--text-secondary)' }}>{v}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* ── Available Tags ── */}
                    <div className="hub-detail-tags-section">
                      <div className="hub-detail-section-title">
                        Available Tags
                        {hubSelectedTag && <span className="hub-detail-selected-tag-badge"><Check size={12} /> {hubSelectedTag}</span>}
                      </div>
                      {hubTagsLoading ? (
                        <div style={{ padding: '1.5rem', textAlign: 'center' }}><LoaderCircle size={24} className="deploy-spin" /></div>
                      ) : (
                        <div className="hub-detail-tags-grid">
                          {hubTags.map((tag) => (
                            <button
                              key={tag.name}
                              type="button"
                              className={`hub-detail-tag-chip ${hubSelectedTag === tag.name ? 'active' : ''}`}
                              onClick={() => handleSelectTag(tag.name)}
                            >
                              {hubSelectedTag === tag.name && <Check size={14} />}
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {hubPortDetecting && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 700 }}>
                          <LoaderCircle size={14} className="deploy-spin" /> Inspecting tag configuration...
                        </div>
                      )}
                    </div>

                    {/* ── Continue Button ── */}
                    <button
                      type="button"
                      className="premium-deploy-btn"
                      onClick={handleHubContinueToDeploy}
                      disabled={hubPortDetecting}
                      style={{ marginTop: '0.5rem' }}
                    >
                      <ChevronRight size={20} />
                      <span>Continue to Deploy{hubSelectedTag ? ` (${hubSelectedImage.name}:${hubSelectedTag})` : ''}</span>
                    </button>
                  </>
                )}
              </div>
            )}

            {hubMode === 'manual' && (
              <form onSubmit={handleHubDeploy} className="vercel-premium-form">

                <div className="form-section-group">
                  <div className="form-section-header">
                    <Cloud size={18} />
                    <h3>Image Details</h3>
                  </div>
                  <div className="form-row-grid">
                    <div className="form-group">
                      <label>Docker Image <span className="required-star">*</span></label>
                      <input type="text" placeholder="e.g. nginx:latest" required value={hubImage} onChange={(e) => setHubImage(e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Application Name <span className="required-star">*</span></label>
                      <input 
                        type="text" 
                        placeholder="e.g. my-app" 
                        required 
                        value={hubAppName} 
                        onChange={handleHubAppNameChange}
                        style={hubAppNameError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
                      />
                      {hubAppNameError ? (
                        <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                          {hubAppNameError}
                        </p>
                      ) : (
                        <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          Use lowercase, numbers, and hyphens only.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section-group">
                  <div className="form-section-header">
                    <Terminal size={18} />
                    <h3>Networking & Classification</h3>
                  </div>
                  <div className="form-row-grid" style={{ alignItems: 'flex-start' }}>
                    <div className="form-group">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                        <label style={{ margin: 0 }}>Internal Port <span className="required-star">*</span></label>
                        {hubPortDetecting && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 700 }}>
                            <LoaderCircle size={12} className="deploy-spin" />
                            Detecting...
                          </div>
                        )}
                        {!hubPortDetecting && hubDetectedPorts.length > 0 && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--success)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <CheckCircle2 size={12} />
                            Auto-detected from image
                          </div>
                        )}
                        {!hubPortDetecting && hubPortFetchFailed && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--accent, #f59e0b)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <AlertCircle size={12} />
                            Optional — will auto-detect after pull
                          </div>
                        )}
                      </div>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="number"
                          placeholder={hubPortFetchFailed ? 'Auto-detect or enter manually (e.g. 80, 3000)' : '80'}
                          disabled={hubPortDetecting}
                          value={hubInternalPort}
                          onChange={(e) => { setHubInternalPort(e.target.value); if (e.target.value) setHubPortFetchFailed(false); }}
                          className={hubPortFetchFailed && !hubInternalPort ? 'input-warning' : ''}
                          style={{ paddingRight: hubPortDetecting ? '2.5rem' : '1rem' }}
                        />
                        {hubPortDetecting && (
                          <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }}>
                            <LoaderCircle size={16} className="deploy-spin" />
                          </div>
                        )}
                      </div>
                      {hubPortFetchFailed && !hubInternalPort && (
                        <p className="form-feedback" style={{ color: 'var(--text-tertiary)', marginTop: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <Info size={13} />
                          Leave empty to auto-detect from the image after pulling, or enter the port manually.
                        </p>
                      )}
                      {hubDetectedPorts.length > 1 && (
                        <div style={{ marginTop: '0.4rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                          {hubDetectedPorts.map((p, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setHubInternalPort(String(p.port))}
                              style={{
                                padding: '0.2rem 0.55rem',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                borderRadius: '6px',
                                border: hubInternalPort === String(p.port) ? '1px solid var(--primary)' : '1px solid var(--border)',
                                background: hubInternalPort === String(p.port) ? 'rgba(var(--primary-rgb), 0.1)' : 'var(--surface-2)',
                                color: hubInternalPort === String(p.port) ? 'var(--primary)' : 'var(--text-secondary)',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              {p.port}/{p.protocol}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Category</label>
                      <select value={hubCategory} onChange={(e) => setHubCategory(e.target.value)}>
                        <option value="frontend">Frontend</option>
                        <option value="backend">Backend</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row-grid">
                    <div className="form-group">
                      <label>Platform Subdomain</label>
                      <div className={`platform-subdomain-group ${subdomainError ? 'has-error' : ''}`}>
                        <input 
                          type="text" 
                          placeholder={hubAppName ? hubAppName.toLowerCase() : "e.g. my-app"} 
                          value={subdomainPrefix} 
                          onChange={handleSubdomainChange}
                        />
                        <span className="platform-subdomain-suffix">
                          {`-${platformSuffix}.devorants.com`}
                        </span>
                      </div>
                      {subdomainError ? (
                        <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                          {subdomainError}
                        </p>
                      ) : (
                        <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          Preview: <strong style={{ color: 'var(--primary, #6366f1)' }}>{`${(subdomainPrefix || hubAppName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')}-${platformSuffix}.devorants.com`}</strong>
                        </p>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Custom Domain (optional)</label>
                      <input 
                        type="text" 
                        placeholder="e.g. app.example.com" 
                        value={hubDomain} 
                        onChange={handleHubDomainChange}
                        style={hubDomainError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
                      />
                      {hubDomainError ? (
                        <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                          {hubDomainError}
                        </p>
                      ) : (
                        <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                          Your own registered domain. Leave blank to use devorants.com subdomain.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="form-row-grid">
                    <div className="form-group">
                      <label>Public Port (Optional)</label>
                      <input type="number" placeholder="Auto-assign" value={selectedHubPort} onChange={(e) => handlePortChange(e.target.value, 'hub')} />
                    </div>
                  </div>
                </div>

                <div className="form-section-group">
                  <div className="form-section-header">
                    <Cpu size={18} />
                    <h3>Hardware Resources</h3>
                  </div>
                  <div className="form-row-grid">
                    <div className="form-group" style={{ display: 'flex', gap: '0.75rem' }}>
                      <div style={{ flex: 1 }}>
                        <label>CPU Limit</label>
                        <select value={hubCpus} onChange={(e) => setHubCpus(e.target.value)} style={{ width: '100%' }}>
                          <option value="1">1 CPU</option>
                          <option value="2">2 CPUs</option>
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label>Memory</label>
                        <select value={hubMemory} onChange={(e) => setHubMemory(e.target.value)} style={{ width: '100%' }}>
                          <option value="256m">256MB</option>
                          <option value="512m">512MB</option>
                          <option value="1g">1GB</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-section-group">
                  <div className="form-section-header">
                    <Database size={18} />
                    <h3>Persistent Volumes (Storage) <span className="required-star">*</span></h3>
                  </div>
                  <div style={{ marginTop: '0.5rem' }}>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginBottom: '0.75rem', lineHeight: '1.4' }}>
                      At least one persistent volume is required to prevent data loss when containers are rebuilt or restarted.
                    </p>

                    {hubVolumes.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem' }}>
                        {hubVolumes.map((vol, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.65rem', background: 'rgba(var(--text-main-rgb), 0.03)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Folder size={14} style={{ color: 'var(--primary)' }} />
                              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)' }}>{vol.name}</span>
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>→ {vol.path}</span>
                              <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: 'var(--surface-3)', borderRadius: '10px', color: 'var(--text-secondary)', marginLeft: '4px' }}>{vol.sizeLimit}</span>
                            </div>
                            <button type="button" onClick={() => handleRemoveHubVolume(idx)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '0.2rem', display: 'flex', alignItems: 'center', borderRadius: '4px' }} onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'rgba(var(--danger-rgb), 0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; e.currentTarget.style.background = 'none'; }}>
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px auto', gap: '0.5rem' }}>
                      <input
                        type="text"
                        placeholder="Volume Name (e.g. data)"
                        value={hubVolumeName}
                        onChange={(e) => setHubVolumeName(e.target.value)}
                        style={{ padding: '0.6rem 0.75rem', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg)', color: 'var(--text-main)' }}
                      />
                      <input
                        type="text"
                        placeholder="Path (e.g. /var/lib/mysql)"
                        value={hubVolumeContainerPath}
                        onChange={(e) => setHubVolumeContainerPath(e.target.value)}
                        style={{ padding: '0.6rem 0.75rem', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg)', color: 'var(--text-main)' }}
                      />
                      <input
                        type="text"
                        placeholder="Size"
                        value={hubVolumeSize}
                        onChange={(e) => setHubVolumeSize(e.target.value)}
                        style={{ padding: '0.6rem 0.75rem', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg)', color: 'var(--text-main)', textAlign: 'center' }}
                      />
                      <button
                        type="button"
                        onClick={handleAddHubVolume}
                        className="vercel-btn-sm"
                        style={{ padding: '0 1rem', height: '100%', background: 'var(--surface-2)', color: 'var(--text-main)', border: '1px solid var(--border)' }}
                      >
                        Add Volume
                      </button>
                    </div>
                    {hubVolumes.length === 0 && (
                      <p className="form-feedback" style={{ color: 'var(--danger)', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <AlertCircle size={13} />
                        You must configure a volume before deploying.
                      </p>
                    )}
                  </div>
                </div>

                <div className="form-section-group">
                  <div className="form-section-header" onClick={() => setHubEnvExpanded(!hubEnvExpanded)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <Settings size={18} />
                    <h3>Environment Variables {hubEnvVars.length > 0 && <span style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 700, marginLeft: '0.5rem' }}>({hubEnvVars.length})</span>}</h3>
                    <ChevronRight size={16} style={{ marginLeft: 'auto', transform: hubEnvExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s ease' }} />
                  </div>
                  {hubEnvExpanded && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginBottom: '0.75rem', lineHeight: '1.4' }}>
                        Add environment variables for your container (e.g. <code style={{ background: 'rgba(var(--text-main-rgb), 0.06)', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.75rem' }}>MYSQL_ROOT_PASSWORD</code>, <code style={{ background: 'rgba(var(--text-main-rgb), 0.06)', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.75rem' }}>POSTGRES_PASSWORD</code>)
                      </p>
                      {hubEnvVars.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.75rem' }}>
                          {hubEnvVars.map((ev, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem', background: 'rgba(var(--text-main-rgb), 0.03)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                              <code style={{ flex: 1, fontSize: '0.8rem', fontWeight: 600, color: 'var(--primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.key}</code>
                              <span style={{ color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>=</span>
                              <code style={{ flex: 2, fontSize: '0.8rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.value || '(empty)'}</code>
                              <button type="button" onClick={() => setHubEnvVars(hubEnvVars.filter((_, i) => i !== idx))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '0.15rem', display: 'flex', alignItems: 'center' }}>
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="form-row-grid" style={{ alignItems: 'flex-end' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontSize: '0.75rem', marginBottom: '0.3rem' }}>Key</label>
                          <input type="text" placeholder="e.g. MYSQL_ROOT_PASSWORD" value={hubEnvKey} onChange={(e) => setHubEnvKey(e.target.value)} />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontSize: '0.75rem', marginBottom: '0.3rem' }}>Value</label>
                          <input type="text" placeholder="e.g. my-secret-pw" value={hubEnvValue} onChange={(e) => setHubEnvValue(e.target.value)} />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!hubEnvKey.trim()) return;
                          setHubEnvVars([...hubEnvVars, { key: hubEnvKey.trim(), value: hubEnvValue.trim() }]);
                          setHubEnvKey('');
                          setHubEnvValue('');
                        }}
                        disabled={!hubEnvKey.trim()}
                        style={{
                          marginTop: '0.5rem',
                          padding: '0.45rem 1rem',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          borderRadius: '6px',
                          border: '1px solid var(--border)',
                          background: hubEnvKey.trim() ? 'rgba(var(--primary-rgb), 0.08)' : 'var(--surface)',
                          color: hubEnvKey.trim() ? 'var(--primary)' : 'var(--text-tertiary)',
                          cursor: hubEnvKey.trim() ? 'pointer' : 'not-allowed',
                          transition: 'all 0.15s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}
                      >
                        <Check size={14} /> Add Variable
                      </button>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={loading || Boolean(hubAppNameError) || !hubAppName.trim() || !hubImage} className="premium-deploy-btn" style={hubAppNameError ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                  {loading ? <LoaderCircle size={20} className="deploy-spin" /> : <Rocket size={20} />}
                  <span>{loading ? 'Importing Image...' : 'Deploy from Hub'}</span>
                </button>
              </form>
            )}
          </div>
  );
}
